import { AlertTriangle, Clock, ExternalLink, Briefcase, TrendingUp, UserPlus, Vote, Crown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatDemandMessage } from '@/lib/factionDemands'
import type {
  Faction,
  FactionDemandDetails,
  FactionDemandType,
  PolicyShiftDemandDetails,
  CandidatePromotionDemandDetails,
  BillSupportDemandDetails,
} from '@/types'

export interface FactionDemandAlertProps {
  faction: Faction
  demandDetails: FactionDemandDetails
  currentTick: number
  onViewFaction?: () => void
  variant?: 'compact' | 'full'
  className?: string
}

// Ticks before deadline is considered urgent
const URGENT_THRESHOLD = 2
const CRITICAL_THRESHOLD = 1

const DEMAND_ICONS: Record<FactionDemandType, React.ComponentType<{ className?: string }>> = {
  cabinet_role: Briefcase,
  policy_shift: TrendingUp,
  candidate_promotion: UserPlus,
  bill_support: Vote,
  leadership_change: Crown,
}

function getUrgencyLevel(ticksRemaining: number): {
  level: 'normal' | 'urgent' | 'critical'
  label: string
  className: string
} {
  if (ticksRemaining <= 0) {
    return {
      level: 'critical',
      label: 'IMMINENT',
      className: 'bg-red-500/20 text-red-400 border-red-500/50 animate-pulse',
    }
  }
  if (ticksRemaining <= CRITICAL_THRESHOLD) {
    return {
      level: 'critical',
      label: `${ticksRemaining} tick`,
      className: 'bg-red-500/20 text-red-400 border-red-500/50',
    }
  }
  if (ticksRemaining <= URGENT_THRESHOLD) {
    return {
      level: 'urgent',
      label: `${ticksRemaining} ticks`,
      className: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
    }
  }
  return {
    level: 'normal',
    label: `${ticksRemaining} ticks`,
    className: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
  }
}

function getDemandActionSuggestion(
  demandType: FactionDemandType,
  demandDetails: FactionDemandDetails
): string {
  switch (demandType) {
    case 'cabinet_role':
      return 'Appoint a faction member to a cabinet position when cabinet appointments become available.'
    case 'policy_shift': {
      const details = demandDetails as PolicyShiftDemandDetails
      const direction = details.direction === 'increase' ? 'more' : 'less'
      return `Support bills that shift party position to be ${direction} ${details.axis_name.toLowerCase()}, or adjust party platform.`
    }
    case 'candidate_promotion': {
      const details = demandDetails as CandidatePromotionDemandDetails
      const roles = details.acceptable_roles.join(', ')
      return `Assign ${details.candidate_name} to one of these roles: ${roles}.`
    }
    case 'bill_support': {
      const details = demandDetails as BillSupportDemandDetails
      return `${details.stance === 'support' ? 'Support' : 'Oppose'} upcoming ${details.bill_category} legislation when voting occurs.`
    }
    case 'leadership_change':
      return 'This is an extreme demand. Consider addressing underlying satisfaction issues or accepting potential defection.'
    default:
      return 'Address the faction\'s concerns to prevent escalation.'
  }
}

/**
 * Compact variant for dashboard alerts area.
 */
function CompactAlert({
  faction,
  demandDetails,
  currentTick,
  onViewFaction,
  className,
}: FactionDemandAlertProps) {
  const demandType = faction.demand_type!
  const ticksRemaining = faction.last_demand_tick
    ? Math.max(0, faction.last_demand_tick + 5 - currentTick) // 5 tick default deadline
    : 3

  const urgency = getUrgencyLevel(ticksRemaining)
  const Icon = DEMAND_ICONS[demandType]

  // Brief summary (first sentence of full message)
  const fullMessage = formatDemandMessage(demandType, demandDetails, faction.name)
  const briefMessage = fullMessage.split('.')[0] + '.'

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors',
        faction.status === 'threatening' ? 'border-orange-500/50 bg-orange-500/5' : 'border-amber-500/50 bg-amber-500/5',
        className
      )}
      onClick={onViewFaction}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onViewFaction) {
          e.preventDefault()
          onViewFaction()
        }
      }}
    >
      {/* Icon */}
      <div
        className={cn(
          'shrink-0 p-2 rounded-full',
          faction.status === 'threatening' ? 'bg-orange-500/20' : 'bg-amber-500/20'
        )}
      >
        <Icon
          className={cn(
            'h-4 w-4',
            faction.status === 'threatening' ? 'text-orange-400' : 'text-amber-400'
          )}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{faction.name}</span>
          {faction.status === 'threatening' && (
            <AlertTriangle className="h-3 w-3 text-orange-400 shrink-0" />
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">{briefMessage}</p>
      </div>

      {/* Deadline badge */}
      <Badge variant="outline" className={cn('shrink-0', urgency.className)}>
        <Clock className="h-3 w-3 mr-1" />
        {urgency.label}
      </Badge>

      {/* View arrow */}
      {onViewFaction && (
        <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
      )}
    </div>
  )
}

/**
 * Full variant for faction detail and dedicated alerts.
 */
function FullAlert({
  faction,
  demandDetails,
  currentTick,
  onViewFaction,
  className,
}: FactionDemandAlertProps) {
  const demandType = faction.demand_type!
  const ticksRemaining = faction.last_demand_tick
    ? Math.max(0, faction.last_demand_tick + 5 - currentTick)
    : 3

  const urgency = getUrgencyLevel(ticksRemaining)
  const Icon = DEMAND_ICONS[demandType]
  const fullMessage = formatDemandMessage(demandType, demandDetails, faction.name)
  const actionSuggestion = getDemandActionSuggestion(demandType, demandDetails)

  const isThreatening = faction.status === 'threatening'
  const borderColor = isThreatening ? 'border-orange-500/50' : 'border-amber-500/50'
  const bgColor = isThreatening ? 'bg-orange-500/5' : 'bg-amber-500/5'

  return (
    <Card className={cn(borderColor, bgColor, className)}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'p-2 rounded-full',
                isThreatening ? 'bg-orange-500/20' : 'bg-amber-500/20'
              )}
            >
              <Icon
                className={cn(
                  'h-5 w-5',
                  isThreatening ? 'text-orange-400' : 'text-amber-400'
                )}
              />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {isThreatening ? 'Ultimatum' : 'Demand'} from {faction.name}
                {isThreatening && (
                  <AlertTriangle className="h-4 w-4 text-orange-400" />
                )}
              </CardTitle>
            </div>
          </div>
          <Badge variant="outline" className={urgency.className}>
            <Clock className="h-3 w-3 mr-1" />
            {urgency.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Main demand message */}
        <p className="text-sm">{fullMessage}</p>

        {/* Deadline warning */}
        {isThreatening && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-orange-500/10 border border-orange-500/30">
            <AlertTriangle className="h-4 w-4 text-orange-400 shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium text-orange-400">Warning:</span>{' '}
              <span className="text-muted-foreground">
                If this demand is not addressed, the faction may defect from the party,
                taking members and influence with them.
              </span>
            </div>
          </div>
        )}

        {/* Action suggestion */}
        <div className="text-sm">
          <span className="font-medium">Suggested action:</span>{' '}
          <span className="text-muted-foreground">{actionSuggestion}</span>
        </div>

        {/* View faction button */}
        {onViewFaction && (
          <Button
            variant="outline"
            size="sm"
            onClick={onViewFaction}
            className="w-full"
          >
            View Faction Details
            <ExternalLink className="h-3 w-3 ml-2" />
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

export function FactionDemandAlert(props: FactionDemandAlertProps) {
  const { variant = 'compact' } = props

  // Don't render if no demand
  if (!props.faction.demand_type || !props.demandDetails) {
    return null
  }

  if (variant === 'compact') {
    return <CompactAlert {...props} />
  }

  return <FullAlert {...props} />
}
